import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "@/components/base/Seo";
import EventCard, { formatViews } from "@/components/feature/EventCard";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { categories, hotEvents, faqs } from "@/mocks/events";

const HERO_IMG =
  "https://readdy.ai/api/search-image?query=dark%20moody%20city%20night%20street%20with%20dramatic%20orange%20and%20crimson%20neon%20lights%20reflecting%20on%20wet%20asphalt%2C%20crowd%20silhouettes%20and%20phone%20screens%20glowing%20in%20the%20distance%2C%20atmospheric%20haze%2C%20high%20contrast%2C%20cinematic%20documentary%20style%2C%20gritty%20realistic%20photography&width=1600&height=900&seq=hero-chigua-2026&orientation=landscape";

const VALUES = [
  { icon: "ri-eye-line", title: "真实第一", text: "坚持路人视角与原始画面，不摆拍、不滤镜，保留事件最真实的瞬间。" },
  { icon: "ri-file-list-3-line", title: "完整呈现", text: "尽量还原事件完整过程与现场对话，不断章取义，让语境清晰可见。" },
  { icon: "ri-scales-line", title: "理性吃瓜", text: "倡导就事论事、客观看待争议，拒绝网络攻击与情绪化评判。" },
  { icon: "ri-flashlight-line", title: "及时更新", text: "紧跟全网热点，第一时间整理现场实拍与事件脉络，掌握事件全貌。" },
];

export default function Home() {
  const [kw, setKw] = useState("");
  const navigate = useNavigate();
  const featured = hotEvents[0];
  const sideList = hotEvents.slice(1, 5);
  const gridList = hotEvents.slice(0, 8);
  const topList = [...hotEvents].sort((a, b) => b.views - a.views).slice(0, 5);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(kw.trim() ? `/category?q=${encodeURIComponent(kw.trim())}` : "/category");
  };

  return (
    <>
      <Seo
        title="吃瓜视频在线 - 热点事件现场实拍合集，路人视角全程记录"
        description="吃瓜视频在线聚合全网热点事件现场实拍，事件合集按主题分类，路人视角真实记录。从冲突现场到对话录音，全程无剪辑呈现，让你第一时间掌握事件全貌。"
        keywords="吃瓜视频在线,吃瓜视频在线现场实拍,吃瓜视频在线事件合集,吃瓜视频在线路人视角,吃瓜视频在线全程记录"
        path="/"
      />

      {/* HERO */}
      <section className="relative flex min-h-[600px] w-full items-center justify-center overflow-hidden">
        <img src={HERO_IMG} alt="吃瓜视频在线热点事件现场实拍背景" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/60 to-background-950/90"></div>
        <div className="relative z-10 w-full max-w-4xl px-4 py-24 text-center md:px-6 md:py-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-background-950/50 px-4 py-1.5 text-xs font-medium text-primary-400 backdrop-blur">
            <i className="ri-fire-line"></i>全网热点事件实时聚合
          </span>
          <h1 className="mt-6 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
            吃瓜视频在线
            <span className="mt-2 block text-lg font-bold text-foreground-300 md:text-2xl">
              现场实拍 · 事件合集 · 路人视角 · 全程记录
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-foreground-400 md:text-base">
            聚合全网热点事件现场实拍，事件合集按主题分类，路人视角真实记录。从冲突现场到对话录音，全程无剪辑呈现，让你第一时间掌握事件全貌。
          </p>

          <form onSubmit={submit} className="mx-auto mt-8 flex max-w-xl items-center gap-2 rounded-full border border-background-300/70 bg-background-950/60 p-2 backdrop-blur focus-within:border-primary-500" role="search">
            <i className="ri-search-line pl-3 text-base text-foreground-500"></i>
            <input
              type="text"
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              placeholder="搜索事件关键词，例如：地铁、夜市、广场舞"
              className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            />
            <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600">
              搜索事件
            </button>
          </form>

          <dl className="mx-auto mt-10 grid max-w-xl grid-cols-3 gap-4">
            {[
              { label: "现场事件", value: "3800+" },
              { label: "主题合集", value: "26 档" },
              { label: "今日更新", value: "120+" },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-background-300/40 bg-background-950/40 px-3 py-4 backdrop-blur">
                <dt className="text-xs text-foreground-500">{s.label}</dt>
                <dd className="mt-1 font-heading text-xl font-black text-primary-400 md:text-2xl">{s.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* 主题分类 */}
      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="flex items-end justify-between gap-4">
          <div>
            <span className="font-label text-xs font-semibold tracking-widest text-primary-500">主题分类</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">按主题，找到你想看的事件</h2>
          </div>
          <Link to="/category" className="hidden items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition hover:text-primary-500 sm:flex">
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group relative flex aspect-[4/5] flex-col justify-end overflow-hidden rounded-lg border border-background-200/60"
            >
              <img src={c.image} alt={`${c.name}事件分类`} className="absolute inset-0 h-full w-full object-cover object-center transition duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/40 to-transparent"></div>
              <div className="relative z-10 p-4 md:p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500/90 text-background-50">
                  <i className={`${c.icon} text-base`}></i>
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950 md:text-lg">{c.name}</h3>
                <p className="mt-1 text-xs text-foreground-400">{c.tagline}</p>
                <p className="mt-3 text-xs font-medium text-primary-400">{c.stats}+ 事件</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新热点 */}
      <section className="bg-background-100/60">
        <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="font-label text-xs font-semibold tracking-widest text-accent-500">最新更新</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">最新热点现场实拍</h2>
            </div>
            <Link to="/category" className="hidden items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition hover:text-primary-500 sm:flex">
              更多事件 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <EventCard event={featured} large />
            </div>
            <div className="flex flex-col gap-5">
              {sideList.map((e) => (
                <div key={e.slug} className="flex gap-4 rounded-lg border border-background-200/60 bg-background-50 p-3 transition hover:border-primary-500/50">
                  <Link to={`/event/${e.slug}`} className="relative block h-[76px] w-[110px] shrink-0 overflow-hidden rounded-md">
                    <img src={e.thumb} alt={`${e.title}现场实拍`} className="h-full w-full object-cover" />
                    <span className="absolute bottom-1 right-1 rounded bg-background-950/80 px-1.5 py-0.5 text-[10px] text-foreground-200">{e.duration}</span>
                  </Link>
                  <div className="flex min-w-0 flex-1 flex-col justify-center">
                    <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-900">
                      <Link to={`/event/${e.slug}`} className="transition hover:text-primary-500">{e.title}</Link>
                    </h3>
                    <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(e.views)}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>{e.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 现场实拍精选 */}
      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="flex items-end justify-between gap-4">
          <div>
            <span className="font-label text-xs font-semibold tracking-widest text-primary-500">现场精选</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">路人视角精选合集</h2>
          </div>
          <Link to="/category/scene" className="hidden items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition hover:text-primary-500 sm:flex">
            现场实拍 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {gridList.map((e) => (
            <EventCard key={e.slug} event={e} />
          ))}
        </div>
      </section>

      {/* 本周热议 TOP */}
      <section className="bg-background-100/60">
        <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <span className="font-label text-xs font-semibold tracking-widest text-accent-500">本周热议</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">播放热度 TOP 5</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-500">
                过去一周播放与讨论热度最高的现场事件。每一条都保留完整语境，帮助你看到事件全貌，理性吃瓜。
              </p>
              <div className="mt-6 space-y-3">
                {topList.map((e, i) => (
                  <Link
                    key={e.slug}
                    to={`/event/${e.slug}`}
                    className="group flex items-center gap-4 rounded-lg border border-background-200/60 bg-background-50 p-3 transition hover:border-primary-500/60"
                  >
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-lg font-black ${i < 3 ? "bg-accent-500 text-foreground-950" : "bg-background-200 text-foreground-600"}`}>
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="line-clamp-1 text-sm font-bold text-foreground-900 group-hover:text-primary-500">{e.title}</h3>
                      <p className="mt-1 text-xs text-foreground-500">{e.categoryName} · {e.time}</p>
                    </div>
                    <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-primary-500"><i className="ri-eye-line"></i>{formatViews(e.views)}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex flex-col">
              <span className="font-label text-xs font-semibold tracking-widest text-primary-500">栏目主张</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">我们坚持的四件事</h2>
              <div className="mt-6 grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
                {VALUES.map((v) => (
                  <div key={v.title} className="flex flex-col rounded-lg border border-background-200/60 bg-background-50 p-5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${v.icon} text-lg`}></i>
                    </span>
                    <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{v.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-500">{v.text}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
              >
                了解吃瓜视频在线 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <span className="font-label text-xs font-semibold tracking-widest text-primary-500">常见问题</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">关于吃瓜视频在线</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-500">
              平台的内容来源、真实性保障与理性吃瓜准则，这里都有答案。点击问题展开查看。
            </p>
            <Link to="/about#faq" className="mt-5 inline-flex items-center gap-1 text-sm text-primary-500 transition hover:text-primary-400">
              前往关于我们页 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="lg:col-span-2">
            <FaqAccordion items={faqs} />
          </div>
        </div>
      </section>
    </>
  );
}